import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DELIVERY_LABELS } from "@/lib/cases";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { CopyLink } from "@/components/app/CopyLink";
import { Tick } from "@/components/app/Tick";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const lbl = "font-mono-label text-[9px] font-semibold text-oath-muted";
const sel = "mt-2 h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-oath-ink";

const F = ({ label, children, className = "" }) => (
  <div className={className}><label className={lbl}>{label}</label><div className="mt-2">{children}</div></div>
);

const Section = ({ n, title, children }) => (
  <section className="rounded-3xl border border-gray-200 bg-white p-6">
    <div className="font-mono-label text-[10px] font-semibold text-oath-accent">{n}</div>
    <h2 className="mt-1 font-display text-lg font-bold text-oath-ink">{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

const EMPTY = {
  first_name: "", last_name: "", dob: "", email: "", phone: "",
  address: { line1: "", line2: "", city: "", state: "", zip: "" },
  household: [], permissible_purpose: "", reference: "", notes: "", priority: "standard", check_type: "records", consent_method: "link",
};

const Household = ({ rows, setRows }) => {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="grid gap-2" data-testid="household-list">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 rounded-xl border border-gray-100 p-2 sm:grid-cols-[1fr_1fr_80px_40px] sm:border-0 sm:p-0" data-testid={`household-row-${i}`}>
          <Input placeholder="Full name" value={r.name} onChange={(e) => set(i, "name", e.target.value)} className="col-span-2 sm:col-span-1" data-testid={`household-name-${i}`} />
          <Input placeholder="Relationship" value={r.relationship} onChange={(e) => set(i, "relationship", e.target.value)} data-testid={`household-relationship-${i}`} />
          <Input placeholder="Age" type="number" min={0} max={120} value={r.age} onChange={(e) => set(i, "age", e.target.value)} data-testid={`household-age-${i}`} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="grid place-items-center rounded-md text-oath-muted hover:bg-gray-100" data-testid={`household-remove-${i}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setRows([...rows, { name: "", relationship: "", age: "" }])} data-testid="household-add-button"
        className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-4 py-2 text-xs text-oath-ink hover:border-gray-400">
        <Plus className="h-3.5 w-3.5" /> Add household member
      </button>
    </div>
  );
};

const Result = ({ result }) => (
  <div className="rounded-3xl border border-emerald-200 bg-white p-8" data-testid="new-case-result">
    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
    <h2 className="mt-4 font-display text-2xl font-bold text-oath-ink">Case {result.case.case_number} opened</h2>
    <p className="mt-2 text-sm text-oath-secondary">The case is waiting for {result.case.subject.first_name}'s consent. Nothing else happens until they sign.</p>
    <div className="mt-5 grid gap-2" data-testid="new-case-deliveries">
      {result.deliveries.map((d) => (
        <div key={d.channel} className="text-sm" data-testid={`delivery-${d.channel}`}>
          <b className="uppercase">{d.channel}</b>: {DELIVERY_LABELS[d.status] || d.status}{d.error ? ` — ${d.error}` : ""}
        </div>
      ))}
    </div>
    <p className="mt-5 text-xs text-oath-secondary">You can also share this single-use consent link directly (valid for 14 days):</p>
    <div className="mt-2"><CopyLink path={result.consent_path} testid="new-case-consent-link" /></div>
    <div className="mt-6 flex gap-3">
      <Link to={`/console/cases/${result.case.id}`} className="rounded-full bg-oath-ink px-5 py-2.5 text-xs font-semibold text-white" data-testid="new-case-view-button">View case</Link>
      <Link to="/console/cases" className="rounded-full border border-gray-300 px-5 py-2.5 text-xs font-semibold text-oath-ink">All cases</Link>
    </div>
  </div>
);

export default function NewCase() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [via, setVia] = useState({ email: true, sms: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [coverage, setCoverage] = useState([]);

  useEffect(() => {
    api.get("/coverage").then(({ data }) => setCoverage(data)).catch(() => setCoverage([]));
  }, []);
  useEffect(() => {
    api.get("/cases/meta").then(({ data }) => {
      setMeta(data);
      setVia({ email: true, sms: data.sms_enabled });
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setA = (k) => (e) => setF({ ...f, address: { ...f.address, [k]: e.target.value } });

  const submit = async (e) => {
    e.preventDefault();
    const deliver_via = Object.keys(via).filter((k) => via[k] && (k !== "email" || f.email));
    if (f.consent_method === "link" && !deliver_via.length) {
      toast.error("Choose how to send the consent request.");
      return;
    }
    setLoading(true);
    try {
      const body = {
        ...f, email: f.email || null, deliver_via,
        household: f.household.filter((h) => h.name.trim()).map((h) => ({ ...h, age: h.age === "" ? null : Number(h.age) })),
      };
      const { data } = await api.post("/cases", body);
      setResult(data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell eyebrow={`— ${(user.org_name || "").toUpperCase()}`} title="Order a verification">
      {result ? <Result result={result} /> : !meta ? <Loader2 className="h-5 w-5 animate-spin text-oath-muted" /> : (
        <form onSubmit={submit} className="grid max-w-4xl gap-5" data-testid="new-case-form">
          <Section n="01" title="Who is being verified">
            <div className="grid gap-4 sm:grid-cols-3">
              <F label="FIRST NAME"><Input required value={f.first_name} onChange={set("first_name")} data-testid="case-first-name-input" /></F>
              <F label="LAST NAME"><Input required value={f.last_name} onChange={set("last_name")} data-testid="case-last-name-input" /></F>
              <F label="DATE OF BIRTH"><Input required type="date" value={f.dob} onChange={set("dob")} data-testid="case-dob-input" /></F>
              <F label="MOBILE PHONE"><Input required value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="case-phone-input" /></F>
              <F label="EMAIL (OPTIONAL)" className="sm:col-span-2"><Input type="email" value={f.email} onChange={set("email")} data-testid="case-email-input" /></F>
            </div>
            <p className="mt-3 text-xs text-oath-secondary">The person will confirm their date of birth and the last 4 digits of this phone before they can sign.</p>
          </Section>
          <Section n="02" title="Address and household to verify">
            <div className="grid gap-4 sm:grid-cols-6">
              <F label="STREET ADDRESS" className="sm:col-span-4"><Input required value={f.address.line1} onChange={setA("line1")} data-testid="case-line1-input" /></F>
              <F label="APT / UNIT" className="sm:col-span-2"><Input value={f.address.line2} onChange={setA("line2")} data-testid="case-line2-input" /></F>
              <F label="CITY" className="sm:col-span-3"><Input required value={f.address.city} onChange={setA("city")} data-testid="case-city-input" /></F>
              <F label="STATE" className="sm:col-span-1">
                <select required value={f.address.state} onChange={setA("state")} className={sel.replace("mt-2 ", "")} data-testid="case-state-select">
                  <option value="">—</option>{meta.states.map((s) => <option key={s}>{s}</option>)}
                </select>
              </F>
              <F label="ZIP" className="sm:col-span-2"><Input required value={f.address.zip} onChange={setA("zip")} data-testid="case-zip-input" /></F>
            </div>
            <div className="mt-6"><label className={lbl}>WHO ELSE IS CLAIMED TO LIVE HERE</label>
              <div className="mt-2"><Household rows={f.household} setRows={(h) => setF({ ...f, household: h })} /></div></div>
          </Section>
          <Section n="03" title="Purpose and details">
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="PERMISSIBLE PURPOSE">
                <select required value={f.permissible_purpose} onChange={set("permissible_purpose")} className={sel.replace("mt-2 ", "")} data-testid="case-purpose-select">
                  <option value="">Select…</option>{Object.keys(meta.purposes).filter((k) => !user.org_allowed_purposes?.length || user.org_allowed_purposes.includes(k)).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}</option>)}
                </select>
              </F>
              <F label="YOUR REFERENCE (POLICY / CLAIM / APPLICATION #)"><Input value={f.reference} onChange={set("reference")} data-testid="case-reference-input" /></F>
              <F label="PRIORITY">
                <div className="flex gap-2">
                  {["standard", "rush"].map((p) => (
                    <button key={p} type="button" onClick={() => setF({ ...f, priority: p })} data-testid={`case-priority-${p}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold ${f.priority === p ? "bg-oath-ink text-white" : "border border-gray-300 text-oath-ink"}`}>
                      {p === "rush" ? "Rush" : "Standard"}
                    </button>
                  ))}
                </div>
              </F>
              <F label="CHECK TYPE" className="sm:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {[["records", "Records only"], ["records_field", "Records + field visit"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setF({ ...f, check_type: k })} data-testid={`case-check-${k}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold ${f.check_type === k ? "bg-oath-ink text-white" : "border border-gray-300 text-oath-ink"}`}>{l}</button>
                  ))}
                </div>
                {f.check_type === "records_field" && <p className={`mt-2 text-xs ${f.address.zip.length >= 3 && !coverage.some((c) => c.zip_prefixes.includes(f.address.zip.slice(0, 3))) ? "text-red-600" : "text-oath-secondary"}`} data-testid="case-field-coverage">{f.address.zip.length >= 3 && !coverage.some((c) => c.zip_prefixes.includes(f.address.zip.slice(0, 3))) ? "This ZIP isn't covered yet — we" : "We"} currently visit {coverage.map((c) => c.city).join(", ") || "…"}. The subject must also authorize the visit.</p>}
              </F>
              <F label="NOTES FOR OUR ANALYSTS (OPTIONAL)" className="sm:col-span-2"><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="case-notes-input" /></F>
            </div>
          </Section>
          <Section n="04" title="Consent">
            {user.org_can_upload_consent && (
              <div className="mb-4 flex flex-wrap gap-2">
                {[["link", "Send a consent request"], ["upload", "Upload our signed consent form"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setF({ ...f, consent_method: k })} data-testid={`case-consent-${k}`}
                    className={`rounded-full px-4 py-2 text-xs font-semibold ${f.consent_method === k ? "bg-oath-ink text-white" : "border border-gray-300 text-oath-ink"}`}>{l}</button>
                ))}
              </div>
            )}
            {f.consent_method === "upload" ? <p className="text-sm text-oath-secondary">After opening the case you'll upload the signed form and attest how it was collected.</p> : (<>
            <div className="grid gap-2 sm:grid-cols-2">
              <Tick checked={via.email && !!f.email} disabled={!f.email} onToggle={() => setVia({ ...via, email: !via.email })} testid="case-deliver-email"
                label="Email" hint={f.email ? `To ${f.email}` : "Add an email address above"} />
              <Tick checked={via.sms} disabled={!meta.sms_enabled} onToggle={() => setVia({ ...via, sms: !via.sms })} testid="case-deliver-sms"
                label="Text message" hint={meta.sms_enabled ? `To ${f.phone || "the mobile number"}` : "Text messaging isn't set up yet"} />
            </div>
            <p className="mt-3 text-xs text-oath-secondary">You'll also get a link to share yourself. The link expires after {meta.consent_days} days.</p>
            </>)}
          </Section>
          <p className="text-xs text-oath-secondary" data-testid="case-credits-note">Uses 1 verification credit ({user.org_credits ?? 0} left). Returned if the person declines or the case is cancelled.</p>
          <button type="submit" disabled={loading} data-testid="new-case-submit-button"
            className="inline-flex w-fit items-center gap-2 rounded-full bg-oath-ink px-7 py-3.5 font-mono-label text-[12px] font-semibold text-white hover:bg-black disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} OPEN CASE & REQUEST CONSENT
          </button>
        </form>
      )}
    </AppShell>
  );
}
